import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Col, Row } from '@storable/canaveral-components';
import { FLIPPER_INTERNATIONALIZATION } from '../../../constants/flippers';
import { MANAGE } from '../../../modules/authorization/actions';
import { BUILDER, NAVIGATION } from '../../../modules/authorization/subjects';
import useFlipper from '../../../modules/react/hooks/useFlipper';
import useCurrentUser from '../../hooks/useCurrentUser';
import NavigationLocalesDropdown from '../../containers/NavigationLocalesDropdown';
import NavigationTree, { NavigationTreeLoading } from '../NavigationTree';
import NavigationSettingsControls from '../NavigationSettingsControls';
import SettingsHeader from '../SettingsHeader';
import SettingsFooter from '../SettingsFooter';
import styles from './styles.scss';

const propTypes = {
  addingPageIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  code: PropTypes.string.isRequired,
  enableSearch: PropTypes.string.isRequired,
  navigation: PropTypes.arrayOf(PropTypes.object),
  navigationPageIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onNavigationChange: PropTypes.func.isRequired,
  onNavigationReset: PropTypes.func.isRequired,
  onNavigationSubmit: PropTypes.func.isRequired,
  onPagesSearch: PropTypes.func.isRequired,
  pages: PropTypes.arrayOf(PropTypes.object).isRequired,
  pagesMenuTitle: PropTypes.string.isRequired,
  searchPagesError: PropTypes.string,
  searchingPages: PropTypes.bool,
  submitNavigationErrors: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
    }).isRequired,
  ),
  submittedNavigation: PropTypes.bool,
  submittingNavigation: PropTypes.bool,
  totalPages: PropTypes.number.isRequired,
  unsubmittedNavigationChanges: PropTypes.bool,
};

const defaultProps = {
  navigation: undefined,
  searchPagesError: undefined,
  searchingPages: undefined,
  submitNavigationErrors: undefined,
  submittedNavigation: undefined,
  submittingNavigation: undefined,
  unsubmittedNavigationChanges: undefined,
};

const NavigationSettings = ({
  addingPageIds,
  code,
  enableSearch,
  navigation,
  navigationPageIds,
  onNavigationChange,
  onNavigationReset,
  onNavigationSubmit,
  onPagesSearch,
  pages,
  pagesMenuTitle,
  searchPagesError,
  searchingPages,
  submitNavigationErrors,
  submittedNavigation,
  submittingNavigation,
  totalPages,
  unsubmittedNavigationChanges,
  ...rest
}) => {
  const currentUser = useCurrentUser();
  const authorized = currentUser.can(MANAGE, BUILDER, NAVIGATION);
  const i18nEnabled = useFlipper(FLIPPER_INTERNATIONALIZATION);

  const authorizationErrors = authorized ? undefined : [
    { code: 0, message: 'You do not have permission to edit the site navigation.' },
  ];

  const onItemAdd = (navigationItem) => {
    if (!navigation) {
      return;
    }

    onNavigationChange([navigationItem, ...navigation]);
  };

  return (
    <React.Fragment>
      <Row>
        <Col xs={12}>
          <SettingsHeader text="Navigation">
            {i18nEnabled && (
              <NavigationLocalesDropdown
                className={styles.locales}
                code={code}
              />
            )}
          </SettingsHeader>
        </Col>
      </Row>
      {authorized && (
        <Row>
          <Col xs={4}>
            <NavigationSettingsControls
              {...rest}
              enablePagesSearch={enableSearch}
              navigationPageIds={navigationPageIds}
              onItemAdd={onItemAdd}
              onPagesSearch={onPagesSearch}
              pages={pages}
              pagesMenuTitle={pagesMenuTitle}
              pagesSearchError={searchPagesError}
              searchingPages={searchingPages}
              totalPages={totalPages}
            />
          </Col>
          <Col xs={8}>
            {navigation ? (
              <NavigationTree
                {...rest}
                className={styles.tree}
                navigation={navigation}
                onChange={onNavigationChange}
              />
            ) : (
              <NavigationTreeLoading
                className={styles.tree}
              />
            )}
          </Col>
        </Row>
      )}
      <SettingsFooter
        disabled={!authorized || !unsubmittedNavigationChanges}
        errors={authorizationErrors || submitNavigationErrors}
        onCancel={onNavigationReset}
        onSubmit={onNavigationSubmit}
        submitting={submittingNavigation}
        success={submittedNavigation && 'Navigation successfully saved'}
      />
    </React.Fragment>
  );
};

NavigationSettings.propTypes = propTypes;
NavigationSettings.defaultProps = defaultProps;

NavigationSettings.fragments = {
  page: gql`
    fragment NavigationSettingsPage on Page {
      ...NavigationTreePage
      ...NavigationSettingsControlsPage
    }

    ${NavigationTree.fragments.page}
    ${NavigationSettingsControls.fragments.page}

  `,
  navigationItem: gql`
    fragment NavigationSettingsNavigationItem on NavigationItem {
      ...NavigationTreeNavigationItem
    }

    ${NavigationTree.fragments.navigationItem}
  `,
};

export default NavigationSettings;
